import { supabase } from '../../../lib/supabase';

const WIKI_PAGE_MODULES = import.meta.glob('../../../../docs/plm-graphwiki/wiki/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

type WikiPage = {
  slug: string;
  title: string;
  category: string;
  path: string;
  content: string;
  excerpt: string;
};

function titleFromMarkdown(content: string, fallback: string) {
  const heading = content.match(/^#\s+(.+)$/m)?.[1]?.trim();
  return heading || fallback;
}

function excerptFromMarkdown(content: string) {
  return content
    .replace(/^#.+$/gm, '')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 280);
}

function wikiSlugFromPath(path: string) {
  return path.split('/').pop()?.replace(/\.md$/, '') || path;
}

function categoryFromPath(path: string) {
  if (path.includes('/concepts/')) return 'concept';
  if (path.includes('/guides/')) return 'guide';
  if (path.includes('/comparisons/')) return 'comparison';
  if (path.includes('/entities/')) return 'entity';
  if (path.endsWith('/index.md')) return 'index';
  return 'page';
}

const WIKI_PAGES: WikiPage[] = Object.entries(WIKI_PAGE_MODULES).map(([path, content]) => {
  const slug = wikiSlugFromPath(path);
  return {
    slug,
    title: titleFromMarkdown(content, slug),
    category: categoryFromPath(path),
    path,
    content,
    excerpt: excerptFromMarkdown(content),
  };
});

function scoreWikiPage(page: WikiPage, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return 0;

  let score = 0;
  const title = page.title.toLowerCase();
  const slug = page.slug.toLowerCase();
  const content = page.content.toLowerCase();

  if (title.includes(normalizedQuery)) score += 8;
  if (slug.includes(normalizedQuery)) score += 6;
  if (content.includes(normalizedQuery)) score += 3;

  const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean);
  for (const term of queryTerms) {
    if (title.includes(term)) score += 2;
    if (slug.includes(term)) score += 2;
    if (content.includes(term)) score += 1;
  }

  return score;
}

/**
 * Tool definitions for the AI agent.
 * Each tool maps to a Supabase query or external API call.
 */

export interface ToolResult {
  tool: string;
  result: any;
  error?: string;
}

export async function searchWikiConcepts(query: string, category?: string, limit = 8): Promise<ToolResult> {
  try {
    const matches = WIKI_PAGES
      .filter((page) => !category || page.category === category)
      .map((page) => ({ page, score: scoreWikiPage(page, query) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(({ page, score }) => ({
        slug: page.slug,
        title: page.title,
        category: page.category,
        excerpt: page.excerpt,
        score,
      }));

    return { tool: 'search_wiki_concepts', result: matches };
  } catch (e: any) {
    return { tool: 'search_wiki_concepts', result: null, error: e.message };
  }
}

export async function readWikiPage(pageRef: string): Promise<ToolResult> {
  try {
    const normalizedRef = pageRef.trim().toLowerCase();
    const page = WIKI_PAGES.find((entry) =>
      entry.slug.toLowerCase() === normalizedRef ||
      entry.title.toLowerCase() === normalizedRef ||
      entry.title.toLowerCase().includes(normalizedRef) ||
      entry.slug.toLowerCase().includes(normalizedRef)
    );

    if (!page) {
      return { tool: 'read_wiki_page', result: null, error: `No wiki page found for: ${pageRef}` };
    }

    return {
      tool: 'read_wiki_page',
      result: {
        slug: page.slug,
        title: page.title,
        category: page.category,
        content: page.content,
      },
    };
  } catch (e: any) {
    return { tool: 'read_wiki_page', result: null, error: e.message };
  }
}

// ─── PLM Tools ──────────────────────────────────────────────────────────────

export async function searchProducts(query: string, status?: string, limit = 10): Promise<ToolResult> {
  try {
    let q = supabase.from('products').select('*').limit(limit);
    if (query) q = q.or(`name.ilike.%${query}%,part_number.ilike.%${query}%,description.ilike.%${query}%`);
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) return { tool: 'search_products', result: null, error: error.message };
    return { tool: 'search_products', result: data };
  } catch (e: any) {
    return { tool: 'search_products', result: null, error: e.message };
  }
}

export async function searchComponents(query: string, componentType?: string, limit = 10): Promise<ToolResult> {
  try {
    let q = supabase.from('components').select('*').limit(limit);
    if (query) q = q.or(`description.ilike.%${query}%,part_number.ilike.%${query}%,manufacturer_part_number.ilike.%${query}%`);
    if (componentType) q = q.eq('component_type', componentType);
    const { data, error } = await q;
    if (error) return { tool: 'search_components', result: null, error: error.message };
    return { tool: 'search_components', result: data };
  } catch (e: any) {
    return { tool: 'search_components', result: null, error: e.message };
  }
}

export async function analyzeBom(bomId: string): Promise<ToolResult> {
  try {
    const [headerRes, itemsRes] = await Promise.all([
      supabase.from('bom_headers').select('*').eq('id', bomId).single(),
      supabase.from('bom_items').select('*').eq('bom_id', bomId).order('sort_order'),
    ]);
    if (headerRes.error) return { tool: 'analyze_bom', result: null, error: headerRes.error.message };
    const items = itemsRes.data || [];
    const totalCost = items.reduce((sum: number, i: any) => sum + (i.unit_cost || 0) * i.quantity, 0);
    const uniqueParts = new Set(items.map((i: any) => i.part_number)).size;
    return {
      tool: 'analyze_bom',
      result: {
        header: headerRes.data,
        items,
        summary: {
          total_items: items.length,
          unique_parts: uniqueParts,
          total_cost: totalCost,
          statuses: items.reduce((acc: any, i: any) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {}),
        },
      },
    };
  } catch (e: any) {
    return { tool: 'analyze_bom', result: null, error: e.message };
  }
}

export async function checkInventory(query: string, status?: string, limit = 10): Promise<ToolResult> {
  try {
    let q = supabase.from('inventory').select('*').limit(limit);
    if (query) q = q.or(`part_number.ilike.%${query}%,description.ilike.%${query}%`);
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) return { tool: 'check_inventory', result: null, error: error.message };
    return { tool: 'check_inventory', result: data };
  } catch (e: any) {
    return { tool: 'check_inventory', result: null, error: e.message };
  }
}

export async function searchChanges(query: string, status?: string, limit = 10): Promise<ToolResult> {
  try {
    let q = supabase.from('change_requests').select('*').limit(limit);
    if (query) q = q.or(`title.ilike.%${query}%,cr_number.ilike.%${query}%,description.ilike.%${query}%`);
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) return { tool: 'search_changes', result: null, error: error.message };
    return { tool: 'search_changes', result: data };
  } catch (e: any) {
    return { tool: 'search_changes', result: null, error: e.message };
  }
}

export async function getWhereUsed(partNumber: string): Promise<ToolResult> {
  try {
    const { data, error } = await supabase
      .from('bom_items')
      .select('*, bom_headers!inner(name, bom_number, status, product_id)')
      .ilike('part_number', `%${partNumber}%`)
      .limit(20);
    if (error) return { tool: 'where_used', result: null, error: error.message };
    return { tool: 'where_used', result: data };
  } catch (e: any) {
    return { tool: 'where_used', result: null, error: e.message };
  }
}

export async function getDashboardSummary(): Promise<ToolResult> {
  try {
    const [products, components, changes, inventory, pos] = await Promise.all([
      supabase.from('products').select('status', { count: 'exact' }),
      supabase.from('components').select('id', { count: 'exact' }),
      supabase.from('change_requests').select('status', { count: 'exact' }),
      supabase.from('inventory').select('status', { count: 'exact' }),
      supabase.from('purchase_orders').select('status', { count: 'exact' }),
    ]);

    const productsByStatus: Record<string, number> = {};
    (products.data || []).forEach((p: any) => { productsByStatus[p.status] = (productsByStatus[p.status] || 0) + 1; });

    const changesByStatus: Record<string, number> = {};
    (changes.data || []).forEach((c: any) => { changesByStatus[c.status] = (changesByStatus[c.status] || 0) + 1; });

    return {
      tool: 'dashboard_summary',
      result: {
        products: { total: products.count || 0, byStatus: productsByStatus },
        components: { total: components.count || 0 },
        changes: { total: changes.count || 0, byStatus: changesByStatus },
        inventory: { total: inventory.count || 0 },
        purchase_orders: { total: pos.count || 0 },
      },
    };
  } catch (e: any) {
    return { tool: 'dashboard_summary', result: null, error: e.message };
  }
}

// ─── GraphRAG Tools ─────────────────────────────────────────────────────────

export async function getEntityGraph(entityType: string, entityId: string, depth = 2): Promise<ToolResult> {
  try {
    // Call the PostgreSQL function that traverses the relationship graph
    const { data, error } = await supabase.rpc('get_neighbors', {
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_max_depth: depth,
    });
    if (error) return { tool: 'entity_graph', result: null, error: error.message };
    return { tool: 'entity_graph', result: { entity: { type: entityType, id: entityId }, neighbors: data, total: data?.length || 0 } };
  } catch (e: any) {
    return { tool: 'entity_graph', result: null, error: e.message };
  }
}

export async function getGraphContext(entityType: string, entityId: string): Promise<ToolResult> {
  try {
    // Call the PostgreSQL function that builds a text summary of the graph
    const { data, error } = await supabase.rpc('build_graph_context', {
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_depth: 2,
    });
    if (error) return { tool: 'graph_context', result: null, error: error.message };
    return { tool: 'graph_context', result: data };
  } catch (e: any) {
    return { tool: 'graph_context', result: null, error: e.message };
  }
}

export async function findRelatedEntities(query: string, entityType?: string, limit = 15): Promise<ToolResult> {
  try {
    // Search across entity relationships for connected items
    // First find entities matching the query, then pull their graph
    let searchQuery = supabase.from('entity_relationships').select(`
      id, source_type, source_id, target_type, target_id, relationship, strength, metadata
    `).limit(limit);

    // We can't search relationship content directly, so search source entities first
    // then return their relationships
    const entities: any[] = [];

    // Search products
    const { data: products } = await supabase.from('products').select('id, name, part_number')
      .or(`name.ilike.%${query}%,part_number.ilike.%${query}%`).limit(5);
    products?.forEach(p => entities.push({ type: 'product', id: p.id, name: p.name, part_number: p.part_number }));

    // Search components
    const { data: components } = await supabase.from('components').select('id, description, part_number')
      .or(`description.ilike.%${query}%,part_number.ilike.%${query}%`).limit(5);
    components?.forEach(c => entities.push({ type: 'component', id: c.id, name: c.description, part_number: c.part_number }));

    // Get relationships for found entities
    const relationships: any[] = [];
    for (const entity of entities.slice(0, 5)) {
      const { data: rels } = await supabase.from('entity_relationships')
        .select('*')
        .or(`and(source_type.eq.${entity.type},source_id.eq.${entity.id}),and(target_type.eq.${entity.type},target_id.eq.${entity.id})`)
        .limit(10);
      if (rels) relationships.push(...rels.map((r: any) => ({ ...r, matched_entity: entity })));
    }

    return {
      tool: 'find_related',
      result: { matched_entities: entities, relationships, total_relationships: relationships.length },
    };
  } catch (e: any) {
    return { tool: 'find_related', result: null, error: e.message };
  }
}

export async function impactAnalysis(entityType: string, entityId: string): Promise<ToolResult> {
  try {
    // Get deep graph context (3 hops) to understand full impact
    const { data: neighbors, error } = await supabase.rpc('get_neighbors', {
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_max_depth: 3,
    });
    if (error) return { tool: 'impact_analysis', result: null, error: error.message };

    // Group by entity type for summary
    const grouped: Record<string, any[]> = {};
    (neighbors || []).forEach((n: any) => {
      if (!grouped[n.entity_type]) grouped[n.entity_type] = [];
      grouped[n.entity_type].push(n);
    });

    // Fetch names for the impacted entities
    const impact: Record<string, any[]> = {};
    for (const [type, items] of Object.entries(grouped)) {
      const ids = items.map((i: any) => i.entity_id);
      let names: any[] = [];
      switch (type) {
        case 'product': {
          const { data } = await supabase.from('products').select('id, name, part_number, status').in('id', ids);
          names = data || [];
          break;
        }
        case 'component': {
          const { data } = await supabase.from('components').select('id, description, part_number, lifecycle_status').in('id', ids);
          names = data || [];
          break;
        }
        case 'bom': {
          const { data } = await supabase.from('bom_headers').select('id, name, bom_number, status').in('id', ids);
          names = data || [];
          break;
        }
        case 'document': {
          const { data } = await supabase.from('documents').select('id, name, doc_number, status').in('id', ids);
          names = data || [];
          break;
        }
        case 'change_request': {
          const { data } = await supabase.from('change_requests').select('id, title, cr_number, status').in('id', ids);
          names = data || [];
          break;
        }
        case 'supplier': {
          const { data } = await supabase.from('suppliers').select('id, name, status').in('id', ids);
          names = data || [];
          break;
        }
        default:
          names = items.map((i: any) => ({ id: i.entity_id }));
      }
      impact[type] = names;
    }

    return {
      tool: 'impact_analysis',
      result: {
        source: { type: entityType, id: entityId },
        impact,
        summary: Object.fromEntries(Object.entries(impact).map(([k, v]) => [k, v.length])),
        total_affected: Object.values(impact).reduce((sum, arr) => sum + arr.length, 0),
      },
    };
  } catch (e: any) {
    return { tool: 'impact_analysis', result: null, error: e.message };
  }
}

// ─── Exa Web Search Tool ────────────────────────────────────────────────────

export async function exaWebSearch(query: string, numResults = 5): Promise<ToolResult> {
  const EXA_API_KEY = import.meta.env.VITE_EXA_API_KEY;
  if (!EXA_API_KEY) {
    return { tool: 'exa_web_search', result: null, error: 'Exa API key not configured. Set VITE_EXA_API_KEY in .env' };
  }

  try {
    const response = await fetch('https://api.exa.ai/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': EXA_API_KEY,
      },
      body: JSON.stringify({
        query,
        numResults,
        useAutoprompt: true,
        type: 'auto',
        contents: {
          text: { maxCharacters: 1000 },
          highlights: { numSentences: 3 },
        },
      }),
    });

    if (!response.ok) {
      return { tool: 'exa_web_search', result: null, error: `Exa API error: ${response.status}` };
    }

    const data = await response.json();
    const results = (data.results || []).map((r: any) => ({
      title: r.title,
      url: r.url,
      text: r.text?.substring(0, 500),
      highlights: r.highlights,
      publishedDate: r.publishedDate,
    }));

    return { tool: 'exa_web_search', result: results };
  } catch (e: any) {
    return { tool: 'exa_web_search', result: null, error: e.message };
  }
}

// ─── Tool Registry ──────────────────────────────────────────────────────────

export const TOOL_DEFINITIONS = [
  {
    name: 'search_wiki_concepts',
    description: 'Search the curated PLM GraphWiki for ontology concepts, guides, comparisons, and roadmap pages. Use for domain meaning, workflow definitions, and roadmap context.',
    parameters: { query: 'string', category: 'string (optional: concept|guide|comparison|entity|index)', limit: 'number (optional, default 8)' },
  },
  {
    name: 'read_wiki_page',
    description: 'Read a specific GraphWiki page in full. Use after searching the wiki or when the user asks about a PLM ontology concept, capability, or roadmap item.',
    parameters: { page_ref: 'string (page title or slug)' },
  },
  {
    name: 'search_products',
    description: 'Search products/circuit designs by name, part number, or description. Use when the user asks about products, designs, or PCBs.',
    parameters: { query: 'string', status: 'string (optional: concept|design|prototype|production|eol)', limit: 'number (optional, default 10)' },
  },
  {
    name: 'search_components',
    description: 'Search electronic components by description, part number, or manufacturer part number. Use for finding parts, ICs, passives, connectors.',
    parameters: { query: 'string', component_type: 'string (optional: ic|passive|connector|mechanical|pcb)', limit: 'number (optional)' },
  },
  {
    name: 'analyze_bom',
    description: 'Get full BOM analysis including all items, costs, and summary. Use when the user asks about a specific BOM or assembly.',
    parameters: { bom_id: 'string (UUID of the BOM header)' },
  },
  {
    name: 'check_inventory',
    description: 'Check inventory levels for components. Use when asking about stock, availability, or low-stock items.',
    parameters: { query: 'string', status: 'string (optional: in-stock|low-stock|out-of-stock|excess)', limit: 'number (optional)' },
  },
  {
    name: 'search_changes',
    description: 'Search engineering change requests by title, CR number, or description. Use for questions about ECRs, ECOs, or design changes.',
    parameters: { query: 'string', status: 'string (optional: submitted|under-review|approved|rejected|implemented)', limit: 'number (optional)' },
  },
  {
    name: 'where_used',
    description: 'Find all BOMs that contain a specific part number. Critical for impact analysis and obsolescence management.',
    parameters: { part_number: 'string' },
  },
  {
    name: 'dashboard_summary',
    description: 'Get a high-level summary of the entire PLM system: product counts, component counts, change requests, inventory status.',
    parameters: {},
  },
  {
    name: 'exa_web_search',
    description: 'Search the web for component datasheets, specifications, alternatives, pricing, availability, standards, or supplier notices. Use only when the answer requires external data that is not in the GraphWiki ontology and not in the PLM system.',
    parameters: { query: 'string', num_results: 'number (optional, default 5)' },
  },
  {
    name: 'entity_graph',
    description: 'Get the relationship graph around a specific entity. Shows all connected products, components, BOMs, documents, change requests, and suppliers within N hops. Use for understanding how entities are connected.',
    parameters: { entity_type: 'string (product|component|bom|document|change_request|change_order|supplier)', entity_id: 'string (UUID)', depth: 'number (optional, default 2)' },
  },
  {
    name: 'graph_context',
    description: 'Get a text summary of an entity\'s full relationship neighborhood. Use this before answering questions about a specific product, component, or change request to understand its full context.',
    parameters: { entity_type: 'string', entity_id: 'string (UUID)' },
  },
  {
    name: 'impact_analysis',
    description: 'Perform a deep impact analysis on an entity. Traces all affected products, BOMs, documents, change requests, and suppliers through the relationship graph (3 hops deep). Use when the user asks "what would be affected if..." or "what depends on...".',
    parameters: { entity_type: 'string', entity_id: 'string (UUID)' },
  },
  {
    name: 'find_related',
    description: 'Search for entities and their relationships by keyword. Finds products/components matching the query then returns their graph connections. Use when the user asks about relationships between things.',
    parameters: { query: 'string', entity_type: 'string (optional)', limit: 'number (optional, default 15)' },
  },
];

export async function executeTool(toolName: string, args: Record<string, any>): Promise<ToolResult> {
  switch (toolName) {
    case 'search_wiki_concepts': return searchWikiConcepts(args.query, args.category, args.limit);
    case 'read_wiki_page': return readWikiPage(args.page_ref);
    case 'search_products': return searchProducts(args.query, args.status, args.limit);
    case 'search_components': return searchComponents(args.query, args.component_type, args.limit);
    case 'analyze_bom': return analyzeBom(args.bom_id);
    case 'check_inventory': return checkInventory(args.query, args.status, args.limit);
    case 'search_changes': return searchChanges(args.query, args.status, args.limit);
    case 'where_used': return getWhereUsed(args.part_number);
    case 'dashboard_summary': return getDashboardSummary();
    case 'exa_web_search': return exaWebSearch(args.query, args.num_results);
    case 'entity_graph': return getEntityGraph(args.entity_type, args.entity_id, args.depth);
    case 'graph_context': return getGraphContext(args.entity_type, args.entity_id);
    case 'impact_analysis': return impactAnalysis(args.entity_type, args.entity_id);
    case 'find_related': return findRelatedEntities(args.query, args.entity_type, args.limit);
    default: return { tool: toolName, result: null, error: `Unknown tool: ${toolName}` };
  }
}
